import { VectorTile } from "@mapbox/vector-tile";
import Protobuf from "pbf";
import { Timer, defaultFocusModel } from "vue-niney";

export function VectorTileCache() {
  this.timeToLive = 16000;
  this.minSize = 40;
  this.maxZoomLevelIndex = 13;
  this.excludedTileZxys = [];
  this.cache = {};

  this.cleanUpTimer = new Timer(Math.trunc(this.timeToLive / 2), -1);
  this.cleanUpTimer.timerHandler = () => this.cleanUp();
  this.cleanUpTimer.start();
}

VectorTileCache.prototype.loadTileData = function(tile, layerName, component) {
  const transform = this.getTransform(tile);
  const url = transform.url;
  const key = url + "|" + layerName;
  if (this.cache[key] != null) {
    if (this.cache[key].listeners == null) {
      this.cache[key].timestamp = Date.now();
      component.loadTileDataHandler(tile, this.cache[key].layers, transform);
    } else {  // There are listeners, so still loading a previous call.
      const i = this.cache[key].listeners.findIndex(listener => (listener.tile.url == tile.url) && (listener.component == component));
      if (i > -1) {
        const listener = this.cache[key].listeners.splice(i, 1)[0];
        listener.component.loadTileDataHandler(listener.tile, null, null, true);
      }
      this.cache[key].listeners.push({tile: tile, component: component, transform: transform});
    }
  } else {
    const tileZxy = url.match(/(\/\d+\/\d+\/\d+)\./)[1];
    if (this.excludedTileZxys.includes(tileZxy)) {
      this.cache[key] = {timestamp: Date.now(), layers: [{extent: 4096, length: 0}]};
      component.loadTileDataHandler(tile, this.cache[key].layers, transform);
    } else {
      this.cache[key] = {timestamp: Date.now(), layers: [], listeners: [{tile: tile, component: component, transform: transform}]};
      const endpoints = this.getEndpoints(layerName);
      let numLoading = endpoints.length;
      endpoints.forEach(endpoint => {
        const endpointUrl = url.replace(/\$L/, endpoint.name);
        fetch(endpointUrl)
          .then(response => {
            if (!response.ok) {
              throw new Error("HTTP error. Status: " + response.status);
            }
            return response.arrayBuffer();
          })
          .catch(error => {
            if (this.cache[key] != null) {
              delete this.cache[key].layers;
            }
            numLoading--;
            this.proceed(key, numLoading);

            return null;  // Sentinel value.
          })
          .then(response => {
            if (response == null) {  // Sentinel check.
              return;
            }

            if ((this.cache[key] != null) && (this.cache[key].layers != null)) {
              const vectorTile = new VectorTile(new Protobuf(response));
              endpoint.layers.forEach(layer => {
                const vectorTileLayer = vectorTile.layers[layer.name] || { extent: 4096, length: 0 };
                if (vectorTileLayer.name != null) {
                  vectorTileLayer["pointsOnly"] = layer.pointsOnly;
                }
                this.cache[key].layers.push(vectorTileLayer);
              });
            }
            numLoading--;
            this.proceed(key, numLoading);
          });
      });
    }
  }
}

VectorTileCache.prototype.getTransform = function(tile) {
  if (this.maxZoomLevelIndex == null) {
    return {zoomFactor: 1, xShiftFactor: 0, yShiftFactor: 0, url: tile.url};
  }

  const srs = defaultFocusModel.srs;
  const tileZoomLevel = srs.getZoomLevel(tile.scale);
  const maxZoomLevel = srs.zoomLevels[this.maxZoomLevelIndex];
  if (tileZoomLevel.zoomLevel <= maxZoomLevel.zoomLevel) {
    return {zoomFactor: 1, xShiftFactor: 0, yShiftFactor: 0, url: tile.url};
  }

  const zoomFactor = Math.pow(2, tileZoomLevel.zoomLevel - maxZoomLevel.zoomLevel);
  const subTileX = Math.round((tile.minX - srs.minX) / maxZoomLevel.resolution / tile.tileWidth * zoomFactor) / zoomFactor;
  const subTileY = Math.round((srs.maxY - tile.maxY) / maxZoomLevel.resolution / tile.tileHeight * zoomFactor) / zoomFactor;
  const tileLimit = Math.pow(2, maxZoomLevel.zoomLevel);
  const tileX = ((Math.floor(subTileX) % tileLimit) + tileLimit) % tileLimit;
  const tileY = Math.max(Math.floor(subTileY), 0);
  const tileZ = maxZoomLevel.zoomLevel;

  return {
    zoomFactor: zoomFactor,
    xShiftFactor: subTileX % 1,
    yShiftFactor: subTileY % 1,
    url: tile.url.replace(/\/(\d+)\/\d+\/\d+\./, "/" + tileZ + "/" + tileX + "/" + tileY + ".")
  };
}

VectorTileCache.prototype.getEndpoints = function(layerName) {
  return [
    {name: "", layers: [
      {name: layerName, pointsOnly: false}
    ]}
  ];
}

VectorTileCache.prototype.proceed = function(key, numLoading) {
  if (numLoading == 0 && this.cache[key] != null) {
    if (this.cache[key].layers != null) {
      this.cache[key].listeners.forEach(listener => {
        listener.component.loadTileDataHandler(listener.tile, this.cache[key].layers, listener.transform);
      });
      delete this.cache[key].listeners;
    } else {
      console.warn("Error loading vector tile: " + key);
      this.cache[key].listeners.forEach(listener => {
        listener.component.loadTileDataHandler(listener.tile, null, null);
      });
      delete this.cache[key];
    }
  }
}

VectorTileCache.prototype.cleanUp = function() {
  const timestamps = Object.values(this.cache).map(c => c.timestamp).sort();
  if (timestamps.length <= this.minSize) {
    return;
  }

  const cleanUpTimestamp = Date.now() - this.timeToLive;
  const minSizeTimestamp = this.minSize? timestamps[timestamps.length - this.minSize]: Number.MAX_SAFE_INTEGER;
  Object.keys(this.cache).forEach(key => {
    if ((this.cache[key].timestamp < cleanUpTimestamp) && (this.cache[key].timestamp < minSizeTimestamp)) {
      delete this.cache[key];
    }
  });
}

export const defaultVectorTileCache = new VectorTileCache();
